import { useState, type ClipboardEvent } from "react";
import { api, message } from "../api";

interface Entry { firstName: string; lastName: string; title: string | null; department: string | null; honorific: string | null }
interface Plan {
  add: Entry[];
  retitle: { id: string; name: string; from: string | null; to: string }[];
  reactivate: { id: string; name: string; title: string | null }[];
  missing: { id: string; name: string; title: string | null }[];
  officials: number;
}
interface Preview { via: "table" | "model"; found: number; dropped: number; plan: Plan }

/**
 * Brings the staff list in line with the city's own directory: read it from
 * its URL or from a paste, review every proposed change, apply what's ticked.
 * Nothing changes until Apply, and nobody is removed unless ticked.
 */
export function DirectoryImport(props: { clientId: string; initialUrl: string | null; onDone: () => void; onCancel: () => void }) {
  const [url, setUrl] = useState(props.initialUrl ?? "");
  const [pasted, setPasted] = useState("");
  /** The clipboard's HTML, which keeps the table the plain text loses. */
  const [pastedHtml, setPastedHtml] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [pick, setPick] = useState({ add: new Set<number>(), retitle: new Set<string>(), reactivate: new Set<string>(), deactivate: new Set<string>() });

  async function read(body: object) {
    setBusy(true); setError(null); setPreview(null);
    try {
      const p = await api.post<Preview>(`/api/clients/${props.clientId}/directory/preview`, body);
      setPreview(p);
      // Additions, title changes and returns are ticked; removals never are.
      setPick({
        add: new Set(p.plan.add.map((_, i) => i)),
        retitle: new Set(p.plan.retitle.map((r) => r.id)),
        reactivate: new Set(p.plan.reactivate.map((r) => r.id)),
        deactivate: new Set(),
      });
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  function onPaste(e: ClipboardEvent<HTMLTextAreaElement>) {
    const html = e.clipboardData.getData("text/html");
    setPastedHtml(html || null);
  }

  async function apply() {
    if (!preview) return;
    const p = preview.plan;
    if (pick.deactivate.size > 0 && !window.confirm(
      `Deactivate ${pick.deactivate.size} staff ${pick.deactivate.size === 1 ? "member" : "members"} the directory no longer lists?\n\n` +
      "They stay named in past meetings and can be reactivated from their Edit screen.")) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/api/clients/${props.clientId}/directory/apply`, {
        add: p.add.filter((_, i) => pick.add.has(i)).map((a) => ({
          firstName: a.firstName, lastName: a.lastName, title: a.title, honorific: a.honorific,
        })),
        retitle: p.retitle.filter((r) => pick.retitle.has(r.id)).map((r) => ({ id: r.id, title: r.to })),
        reactivate: [...pick.reactivate],
        deactivate: [...pick.deactivate],
      });
      props.onDone();
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  const toggle = <K extends keyof typeof pick>(k: K, v: (typeof pick)[K] extends Set<infer T> ? T : never) =>
    setPick((s) => {
      const next = new Set(s[k] as Set<typeof v>);
      if (next.has(v)) next.delete(v); else next.add(v);
      return { ...s, [k]: next };
    });

  const total = preview ? preview.plan.add.length + preview.plan.retitle.length + preview.plan.reactivate.length + preview.plan.missing.length : 0;
  const chosen = pick.add.size + pick.retitle.size + pick.reactivate.size + pick.deactivate.size;

  return (
    <div className="card card--form directory-import">
      <h2>Import from the staff directory</h2>
      {error && <div className="alert" role="alert">{error}</div>}

      {!preview && (
        <>
          <div className="field">
            <label htmlFor="dirurl">Directory page</label>
            <div className="inline-form">
              <input id="dirurl" type="url" value={url} placeholder="https://www.example.gov/staff-directory"
                     onChange={(e) => setUrl(e.target.value)} />
              <button className="btn btn--inline" disabled={busy || !url} onClick={() => void read({ url })}>
                {busy ? "Reading…" : "Read page"}
              </button>
            </div>
            <p className="hint">Many city sites refuse automated requests. If this one does, paste the list below instead.</p>
          </div>
          <div className="field">
            <label htmlFor="dirpaste">Or paste it</label>
            <textarea id="dirpaste" rows={6} value={pasted} onPaste={onPaste}
                      placeholder="Open the directory in your browser, select the staff table, copy, and paste here."
                      onChange={(e) => { setPasted(e.target.value); if (!e.target.value) setPastedHtml(null); }} />
            <button className="btn btn--inline" disabled={busy || !pasted.trim()}
                    onClick={() => void read(pastedHtml ? { html: pastedHtml } : { text: pasted })}>
              {busy ? "Reading…" : "Read pasted list"}
            </button>
          </div>
          <button className="linkbtn" onClick={props.onCancel}>Cancel</button>
        </>
      )}

      {preview && (
        <>
          <p className="muted small">
            Found {preview.found} staff{preview.via === "model" ? " (read by AI; every name checked against the page)" : ""}.
            {preview.dropped > 0 && ` ${preview.dropped} entries couldn't be verified and were left out.`}
            {preview.plan.officials > 0 && ` ${preview.plan.officials} matched officials, whose seats are edited separately.`}
            {total === 0 && " The roster already matches."}
          </p>

          {preview.plan.add.length > 0 && (
            <Section title={`New (${preview.plan.add.length})`}>
              {preview.plan.add.map((a, i) => (
                <Row key={i} checked={pick.add.has(i)} onToggle={() => toggle("add", i)}>
                  <strong>{a.firstName} {a.lastName}</strong>{a.title ? ` — ${a.title}` : ""}
                  {a.honorific && <span className="muted"> · later mentions: {a.honorific} {a.lastName}</span>}
                </Row>
              ))}
            </Section>
          )}
          {preview.plan.retitle.length > 0 && (
            <Section title={`Title changed (${preview.plan.retitle.length})`}>
              {preview.plan.retitle.map((r) => (
                <Row key={r.id} checked={pick.retitle.has(r.id)} onToggle={() => toggle("retitle", r.id)}>
                  <strong>{r.name}</strong>: <span className="muted">{r.from ?? "no title"}</span> → {r.to}
                </Row>
              ))}
            </Section>
          )}
          {preview.plan.reactivate.length > 0 && (
            <Section title={`Listed again (${preview.plan.reactivate.length})`}>
              {preview.plan.reactivate.map((r) => (
                <Row key={r.id} checked={pick.reactivate.has(r.id)} onToggle={() => toggle("reactivate", r.id)}>
                  <strong>{r.name}</strong> — reactivate{r.title ? ` (${r.title})` : ""}
                </Row>
              ))}
            </Section>
          )}
          {preview.plan.missing.length > 0 && (
            <Section title={`Not in the directory (${preview.plan.missing.length})`}
                     note="Tick only people who have left. Unticked, they stay as they are.">
              {preview.plan.missing.map((m) => (
                <Row key={m.id} checked={pick.deactivate.has(m.id)} onToggle={() => toggle("deactivate", m.id)}>
                  <strong>{m.name}</strong>{m.title ? ` — ${m.title}` : ""} — deactivate
                </Row>
              ))}
            </Section>
          )}

          <div className="actions">
            {total > 0 && (
              <button className="btn btn--inline" disabled={busy || chosen === 0} onClick={() => void apply()}>
                {busy ? "Applying…" : `Apply ${chosen} ${chosen === 1 ? "change" : "changes"}`}
              </button>
            )}
            <button className="linkbtn" onClick={() => setPreview(null)} disabled={busy}>Read again</button>
            <button className="linkbtn" onClick={props.onCancel} disabled={busy}>Close</button>
          </div>
        </>
      )}
    </div>
  );
}

function Section(props: { title: string; note?: string; children: React.ReactNode }) {
  return (
    <div className="directory-section">
      <h3>{props.title}</h3>
      {props.note && <p className="hint">{props.note}</p>}
      {props.children}
    </div>
  );
}

function Row(props: { checked: boolean; onToggle: () => void; children: React.ReactNode }) {
  return (
    <label className="check directory-row">
      <input type="checkbox" checked={props.checked} onChange={props.onToggle} />
      <span>{props.children}</span>
    </label>
  );
}

type SeatRole = "presiding" | "pro_tem" | "member";
interface MemberPlan {
  add: { firstName: string; lastName: string; title: string | null; role: SeatRole }[];
  join: { personId: string; name: string; role: SeatRole; reactivate: boolean }[];
  reseat: { membershipId: string; name: string; from: string; to: SeatRole }[];
  leaving: { membershipId: string; name: string; role: string }[];
  staff: number;
}
interface MemberPreview { via: "table" | "model"; found: number; dropped: number; plan: MemberPlan }

/**
 * Brings one body's seats in line with the city's own page for it -- the
 * council's, a commission's: read from its URL or a paste, every change
 * reviewed, only what's ticked applied. New members are added as officials.
 */
export function MembersImport(props: {
  body: { id: string; name: string; presidingTitle: string; memberTitle: string; proTemTitle: string | null };
  onDone: () => void; onCancel: () => void;
}) {
  const [url, setUrl] = useState("");
  const [pasted, setPasted] = useState("");
  const [pastedHtml, setPastedHtml] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<MemberPreview | null>(null);
  const [pick, setPick] = useState({ add: new Set<number>(), join: new Set<string>(), reseat: new Set<string>(), remove: new Set<string>() });
  const seatName = (role: string) => role === "presiding" ? props.body.presidingTitle
    : role === "pro_tem" ? props.body.proTemTitle ?? "Pro Tem" : role === "member" ? props.body.memberTitle : role;

  async function read(body: object) {
    setBusy(true); setError(null); setPreview(null);
    try {
      const p = await api.post<MemberPreview>(`/api/bodies/${props.body.id}/members/preview`, body);
      setPreview(p);
      // New members, joins and seat changes are ticked; removals never are.
      setPick({
        add: new Set(p.plan.add.map((_, i) => i)),
        join: new Set(p.plan.join.map((j) => j.personId)),
        reseat: new Set(p.plan.reseat.map((r) => r.membershipId)),
        remove: new Set(),
      });
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  async function apply() {
    if (!preview) return;
    const p = preview.plan;
    if (pick.remove.size > 0 && !window.confirm(
      `Take ${pick.remove.size} ${pick.remove.size === 1 ? "person" : "people"} off the ${props.body.name}?\n\n` +
      "They stay on the roster and in past meetings; only this seat goes.")) return;
    setBusy(true); setError(null);
    try {
      await api.post(`/api/bodies/${props.body.id}/members/apply`, {
        add: p.add.filter((_, i) => pick.add.has(i)).map((a) => ({ firstName: a.firstName, lastName: a.lastName, role: a.role })),
        join: p.join.filter((j) => pick.join.has(j.personId)).map((j) => ({ personId: j.personId, role: j.role, reactivate: j.reactivate })),
        reseat: p.reseat.filter((r) => pick.reseat.has(r.membershipId)).map((r) => ({ membershipId: r.membershipId, role: r.to })),
        remove: [...pick.remove],
      });
      props.onDone();
    } catch (err) { setError(message(err)); }
    finally { setBusy(false); }
  }

  const toggle = <K extends keyof typeof pick>(k: K, v: (typeof pick)[K] extends Set<infer T> ? T : never) =>
    setPick((s) => {
      const next = new Set(s[k] as Set<typeof v>);
      if (next.has(v)) next.delete(v); else next.add(v);
      return { ...s, [k]: next };
    });

  const total = preview ? preview.plan.add.length + preview.plan.join.length + preview.plan.reseat.length + preview.plan.leaving.length : 0;
  const chosen = pick.add.size + pick.join.size + pick.reseat.size + pick.remove.size;

  return (
    <div className="card card--form directory-import">
      <h2>Import members of the {props.body.name}</h2>
      {error && <div className="alert" role="alert">{error}</div>}

      {!preview && (
        <>
          <div className="field">
            <label htmlFor="memurl">The city's page for the {props.body.name}</label>
            <div className="inline-form">
              <input id="memurl" type="url" value={url} placeholder="https://www.example.gov/city-council"
                     onChange={(e) => setUrl(e.target.value)} />
              <button className="btn btn--inline" disabled={busy || !url} onClick={() => void read({ url })}>
                {busy ? "Reading…" : "Read page"}
              </button>
            </div>
            <p className="hint">Many city sites refuse automated requests. If this one does, paste the members below instead.</p>
          </div>
          <div className="field">
            <label htmlFor="mempaste">Or paste them</label>
            <textarea id="mempaste" rows={6} value={pasted} onPaste={(e) => setPastedHtml(e.clipboardData.getData("text/html") || null)}
                      placeholder="Open the page in your browser, select the list of members, copy, and paste here."
                      onChange={(e) => { setPasted(e.target.value); if (!e.target.value) setPastedHtml(null); }} />
            <button className="btn btn--inline" disabled={busy || !pasted.trim()}
                    onClick={() => void read(pastedHtml ? { html: pastedHtml } : { text: pasted })}>
              {busy ? "Reading…" : "Read pasted list"}
            </button>
          </div>
          <button className="linkbtn" onClick={props.onCancel}>Cancel</button>
        </>
      )}

      {preview && (
        <>
          <p className="muted small">
            Found {preview.found} {preview.found === 1 ? "member" : "members"}{preview.via === "model" ? " (read by AI; every name checked against the page)" : ""}.
            {preview.dropped > 0 && ` ${preview.dropped} entries couldn't be verified and were left out.`}
            {preview.plan.staff > 0 && ` ${preview.plan.staff} matched staff, whose seats are edited separately.`}
            {total === 0 && " The seats already match."}
          </p>

          {preview.plan.add.length > 0 && (
            <Section title={`New (${preview.plan.add.length})`} note="Added to the roster as officials. Set a pronoun from their Edit screen if the minutes should use one.">
              {preview.plan.add.map((a, i) => (
                <Row key={i} checked={pick.add.has(i)} onToggle={() => toggle("add", i)}>
                  <strong>{a.firstName} {a.lastName}</strong> — {seatName(a.role)}
                </Row>
              ))}
            </Section>
          )}
          {preview.plan.join.length > 0 && (
            <Section title={`On the roster, not yet on this body (${preview.plan.join.length})`}>
              {preview.plan.join.map((j) => (
                <Row key={j.personId} checked={pick.join.has(j.personId)} onToggle={() => toggle("join", j.personId)}>
                  <strong>{j.name}</strong> — {seatName(j.role)}{j.reactivate ? " (reactivated)" : ""}
                </Row>
              ))}
            </Section>
          )}
          {preview.plan.reseat.length > 0 && (
            <Section title={`Seat changed (${preview.plan.reseat.length})`}>
              {preview.plan.reseat.map((r) => (
                <Row key={r.membershipId} checked={pick.reseat.has(r.membershipId)} onToggle={() => toggle("reseat", r.membershipId)}>
                  <strong>{r.name}</strong>: <span className="muted">{seatName(r.from)}</span> → {seatName(r.to)}
                </Row>
              ))}
            </Section>
          )}
          {preview.plan.leaving.length > 0 && (
            <Section title={`Not on the page (${preview.plan.leaving.length})`}
                     note="Tick only people who have left this body. Unticked, they keep their seat.">
              {preview.plan.leaving.map((l) => (
                <Row key={l.membershipId} checked={pick.remove.has(l.membershipId)} onToggle={() => toggle("remove", l.membershipId)}>
                  <strong>{l.name}</strong> — {seatName(l.role)} — take off this body
                </Row>
              ))}
            </Section>
          )}

          <div className="actions">
            {total > 0 && (
              <button className="btn btn--inline" disabled={busy || chosen === 0} onClick={() => void apply()}>
                {busy ? "Applying…" : `Apply ${chosen} ${chosen === 1 ? "change" : "changes"}`}
              </button>
            )}
            <button className="linkbtn" onClick={() => setPreview(null)} disabled={busy}>Read again</button>
            <button className="linkbtn" onClick={props.onCancel} disabled={busy}>Close</button>
          </div>
        </>
      )}
    </div>
  );
}
